import { Fragment, useEffect, useMemo, useState } from 'react'
import NumberFormat from 'react-number-format'
import { useDispatch, useSelector } from 'react-redux'
import Loading from 'components/loading/Loading'
import NoneAnchor from 'components/NoneAnchor'
import { showAdGroupDailyBudgetEdit } from 'features/dashboard/operation/adGroupDailyBudgetEdit/adGroupDailyBudgetEditSlice'
import { showAdGroupMaxBiddingAmountEdit } from 'features/dashboard/operation/adGroupMaxBiddingAmountEdit/adGroupMaxBiddingAmountEditSlice'
import { fetchDashboardInfoAdGroup } from 'features/dashboard/info/dashboardInfoSlice'
import { CampaignTypes, OperationStatus } from 'features/Codes'
import { equalTo } from 'utils'
import Tooltip from 'components/tooltip/Tooltip'
import PropTypes from 'prop-types'
import { showAdGroupDurationEdit } from 'features/dashboard/operation/adGroupDurationEdit/adGroupDurationEditSlice'
import { showAdGroupsWeightEdit } from 'features/dashboard/operation/adGroupWeightEdit/adGroupWeightEditSlice'
import { showAdGroupPlacementEdit } from 'features/dashboard/operation/adGroupPlacementEdit/adGroupPlacementEditSlice'
import { showAdGroupDeviceEdit } from 'features/dashboard/operation/adGroupDeviceEdit/adGroupDeviceEditSlice'
import { showAdGroupWeeklyScheduleEdit } from 'features/dashboard/operation/adGroupWeeklyScheduleEdit/adGroupWeeklyScheduleEditSlice'
import { everyTime, toInt } from 'components/TimeTable'
import { showAdGroupContentMatchingMaxBiddingAmountEdit } from 'features/dashboard/operation/adGroupContentMatchingMaxBiddingAmountEdit/adGroupContentMatchingMaxBiddingAmountEditSlice'
import { isNil } from 'lodash'

const AdGroupDashboardTitleTooltip = ({ status }) => {
  const [first] = status
  return first ? (
    <Tooltip
      position="bottom"
      content={status.map((item, index) => (
        <Fragment key={index}>
          <strong className="tit_tooltip">운영 불가({item.title})</strong>
          <p className="txt_tooltip2">{item.msg}</p>
        </Fragment>
      ))}>
      운영 불가({first.title}){status.length > 1 ? `(${status.length})` : ''}
    </Tooltip>
  ) : null
}
AdGroupDashboardTitleTooltip.propTypes = {
  status: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      title: PropTypes.string,
      msg: PropTypes.string,
    })
  ),
}

const AdGroupInfo = () => {
  const dispatch = useDispatch()
  const {
    isLoading,
    visible,
    loaded,
    id: adGroupId,
    adGroup: { basic, operation },
  } = useSelector(state => state.dashboard.info)
  const operationStatus = useMemo(
    () => basic?.operationStatus?.map(status => OperationStatus[status]) || [],
    [basic]
  )
  const deviceType = useMemo(() => {
    if (isNil(basic?.deviceSetting?.type)) {
      return null
    }
    return basic?.deviceSetting?.type === 'ALL'
      ? '전체'
      : basic?.deviceSetting?.deviceTypes
          ?.map(device => (device === 'MOBILE' ? '모바일' : device))
          ?.join(', ')
  }, [basic])
  const [tabName, setTabName] = useState('basicInfoTab')
  const everyTimeInt = toInt(everyTime)
  useEffect(() => {
    setTabName('basicInfoTab')
    if (visible && !loaded) {
      dispatch(fetchDashboardInfoAdGroup({ id: adGroupId }))
    }
  }, [dispatch, visible, loaded, adGroupId])

  return (
    <>
      <ul className="tab_g6">
        <li className={tabName === 'basicInfoTab' ? 'on' : ''}>
          <NoneAnchor
            className="link_tab"
            onClick={() => setTabName('basicInfoTab')}>
            기본 정보
          </NoneAnchor>
        </li>
        <li className={tabName === 'operationInfoTab' ? 'on' : ''}>
          <NoneAnchor
            className="link_tab"
            onClick={() => setTabName('operationInfoTab')}>
            운영 정보
          </NoneAnchor>
        </li>
      </ul>
      {isLoading ? (
        <div style={{ height: 296 }}>
          <Loading />
        </div>
      ) : (
        <>
          {tabName === 'basicInfoTab' && loaded && (
            <div className="tab_cont">
              <h4 className="screen_out">기본 정보</h4>
              <div className="infosum_dash">
                <table className="tbl_infosum">
                  <caption className="hide_caption">
                    기본 정보 데이터 테이블
                  </caption>
                  <colgroup>
                    <col />
                    <col />
                  </colgroup>
                  <tbody>
                    <tr>
                      <th>
                        <div className="inner_cell">ID</div>
                      </th>
                      <td>
                        <div className="inner_cell">{adGroupId}</div>
                      </td>
                    </tr>
                    <tr>
                      <th>
                        <div className="inner_cell">상태</div>
                      </th>
                      <td>
                        <div className="inner_cell">
                          {operationStatus.find(equalTo(OperationStatus.ON)) ? (
                            <>운영 가능</>
                          ) : (
                            <AdGroupDashboardTitleTooltip
                              status={operationStatus}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th>
                        <div className="inner_cell">매체유형</div>
                      </th>
                      <td>
                        <div className="inner_cell">
                          {basic?.adGroupType === CampaignTypes.TALK_CHANNEL ? (
                            '검색 매체 카카오'
                          ) : (
                            <NoneAnchor
                              className="link_budget"
                              onClick={() =>
                                dispatch(showAdGroupPlacementEdit([adGroupId]))
                              }>
                              {basic?.placement?.all === true ? '전체' : '일부'}{' '}
                              매체
                            </NoneAnchor>
                          )}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th>
                        <div className="inner_cell">디바이스</div>
                      </th>
                      <td>
                        <div className="inner_cell">
                          {basic?.adGroupType === CampaignTypes.TALK_CHANNEL ? (
                            deviceType
                          ) : (
                            <NoneAnchor
                              className="link_budget"
                              onClick={() =>
                                dispatch(showAdGroupDeviceEdit([adGroupId]))
                              }>
                              {deviceType}
                            </NoneAnchor>
                          )}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th>
                        <div className="inner_cell">
                          {basic?.adGroupType === CampaignTypes.TALK_CHANNEL
                            ? '과금단가'
                            : '기본 입찰가'}
                        </div>
                      </th>
                      <td>
                        <div className="inner_cell">
                          {basic?.adGroupType === CampaignTypes.TALK_CHANNEL ? (
                            <span className="txt_price">
                              <span className="price_sale">
                                <span className="screen_out">정상가</span>
                                3,000원
                              </span>
                              <span className="price_induce">
                                → 할인가 2,000원
                              </span>
                            </span>
                          ) : (
                            <NoneAnchor
                              className="link_budget"
                              onClick={() => {
                                dispatch(
                                  showAdGroupMaxBiddingAmountEdit([adGroupId])
                                )
                              }}>
                              <NumberFormat
                                value={
                                  basic?.biddingStrategy?.maxBiddingAmount || 0
                                }
                                displayType="text"
                                thousandSeparator
                                suffix="원"
                              />
                            </NoneAnchor>
                          )}
                        </div>
                      </td>
                    </tr>
                    {basic?.adGroupType === CampaignTypes.PREMIUM_LINK && (
                      <>
                        <tr>
                          <th>
                            <div className="inner_cell">콘텐츠 매체 입찰가</div>
                          </th>
                          <td>
                            <div className="inner_cell">
                              <NoneAnchor
                                className="link_budget"
                                onClick={() => {
                                  dispatch(
                                    showAdGroupContentMatchingMaxBiddingAmountEdit(
                                      [adGroupId]
                                    )
                                  )
                                }}>
                                {basic?.biddingStrategy
                                  ?.customContentMatchingMaxBiddingAmountYn ===
                                'N'
                                  ? '미설정'
                                  : `${basic?.biddingStrategy?.contentMatchingMaxBiddingAmount?.toLocaleString()}원`}
                              </NoneAnchor>
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <th>
                            <div className="inner_cell">입찰가중치</div>
                          </th>
                          <td>
                            <div className="inner_cell">
                              {basic?.biddingStrategy ? (
                                <>
                                  <p className="txt_cell">
                                    <NoneAnchor
                                      className="link_budget"
                                      onClick={() =>
                                        dispatch(
                                          showAdGroupsWeightEdit([adGroupId])
                                        )
                                      }>
                                      모바일 :{' '}
                                      {basic?.biddingStrategy?.mobileWeight}%
                                    </NoneAnchor>
                                  </p>
                                  <p className="txt_cell">
                                    <NoneAnchor
                                      className="link_budget"
                                      onClick={() =>
                                        dispatch(
                                          showAdGroupsWeightEdit([adGroupId])
                                        )
                                      }>
                                      PC : {basic?.biddingStrategy?.pcWeight}%
                                    </NoneAnchor>
                                  </p>
                                  <p className="txt_cell">
                                    <NoneAnchor
                                      className="link_budget"
                                      onClick={() =>
                                        dispatch(
                                          showAdGroupsWeightEdit([adGroupId])
                                        )
                                      }>
                                      키워드확장 :{' '}
                                      {
                                        basic?.biddingStrategy
                                          ?.keywordExtensionWeight
                                      }
                                      %
                                    </NoneAnchor>
                                  </p>
                                </>
                              ) : (
                                '-'
                              )}
                            </div>
                          </td>
                        </tr>
                      </>
                    )}
                    <tr>
                      <th>
                        <div className="inner_cell">일 예산</div>
                      </th>
                      <td>
                        <div className="inner_cell">
                          <NoneAnchor
                            className="link_budget"
                            onClick={() => {
                              dispatch(showAdGroupDailyBudgetEdit([adGroupId]))
                            }}>
                            <NumberFormat
                              value={basic?.dailyBudget || 0}
                              displayType="text"
                              thousandSeparator
                              suffix="원"
                            />
                          </NoneAnchor>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th>
                        <div className="inner_cell">집행기간</div>
                      </th>
                      <td>
                        <div className="inner_cell">
                          <NoneAnchor
                            className="link_budget"
                            onClick={() => {
                              dispatch(showAdGroupDurationEdit([adGroupId]))
                            }}>
                            {basic?.impressionSchedule?.startDate} -{' '}
                            {basic?.impressionSchedule?.endDate ||
                              '종료일 미설정'}
                          </NoneAnchor>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th>
                        <div className="inner_cell">집행요일/시간</div>
                      </th>
                      <td>
                        <div className="inner_cell">
                          <NoneAnchor
                            className="link_budget"
                            onClick={() => {
                              dispatch(
                                showAdGroupWeeklyScheduleEdit([adGroupId])
                              )
                            }}>
                            {basic?.impressionSchedule?.weeklySchedule ===
                            everyTimeInt
                              ? '모든 요일/시간'
                              : '일부 요일/시간'}
                          </NoneAnchor>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {tabName === 'operationInfoTab' && loaded && (
            <div className="tab_cont">
              <h4 className="screen_out">운영 정보</h4>
              <div className="infosum_dash">
                <table className="tbl_infosum">
                  <caption className="hide_caption">
                    운영 정보 데이터 테이블
                  </caption>
                  <colgroup>
                    <col />
                    <col />
                  </colgroup>
                  <tbody>
                    <tr>
                      <th>
                        <div className="inner_cell">키워드</div>
                      </th>
                      <td>
                        <div className="inner_cell">
                          심사승인{' '}
                          <NumberFormat
                            value={operation.approvedKeywordCount}
                            displayType="text"
                            thousandSeparator
                          />
                          <br />
                          심사중{' '}
                          <NumberFormat
                            value={operation.approvingKeywordCount}
                            displayType="text"
                            thousandSeparator
                          />
                          <br />
                          심사보류{' '}
                          <NumberFormat
                            value={operation.rejectedApprovalKeywordCount}
                            displayType="text"
                            thousandSeparator
                          />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th>
                        <div className="inner_cell">소재</div>
                      </th>
                      <td>
                        <div className="inner_cell">
                          심사승인{' '}
                          <NumberFormat
                            value={operation.approvedCreativeCount}
                            displayType="text"
                            thousandSeparator
                          />
                          <br />
                          심사중{' '}
                          <NumberFormat
                            value={operation.approvingCreativeCount}
                            displayType="text"
                            thousandSeparator
                          />
                          <br />
                          심사보류{' '}
                          <NumberFormat
                            value={operation.rejectedApprovalCreativeCount}
                            displayType="text"
                            thousandSeparator
                          />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </>
  )
}
export default AdGroupInfo
